import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  AlertCircle,
  LoaderCircle,
  MessageSquare,
  Minus,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import api from '../../api';
import robotAssistant from '../../assets/i4-assistant-robot-greeting.png';
import { InventoryChart, isInventoryChart } from './InventoryChart';

const createConversationId = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `ibi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const renderInlineMarkdown = (text) => text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean).map((part, index) => {
  if (part.startsWith('**') && part.endsWith('**')) {
    return <strong key={`${part}-${index}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
  }
  if (part.startsWith('`') && part.endsWith('`')) {
    return <code key={`${part}-${index}`} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.78rem] font-medium text-primary dark:bg-slate-700/80">{part.slice(1, -1)}</code>;
  }
  return part;
});

const tableCells = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());
const isTableSeparator = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const AssistantAnswer = ({ answer }) => {
  const blocks = [];
  let listItems = [];
  const lines = answer.split('\n');

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="my-3 space-y-1.5 rounded-xl border border-slate-100 bg-slate-50/80 p-2.5 dark:border-slate-700/80 dark:bg-slate-900/35">
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`} className="flex gap-2 text-[0.82rem] leading-5 text-foreground">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{renderInlineMarkdown(item)}</span>
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (line.includes('|') && lines[index + 1] && isTableSeparator(lines[index + 1])) {
      flushList();
      const headers = tableCells(line);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
        rows.push(tableCells(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push(
        <div key={`table-${blocks.length}`} className="my-3 overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700/80">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground dark:bg-slate-900/60">
              <tr>{headers.map((header, headerIndex) => <th key={`${header}-${headerIndex}`} className="whitespace-nowrap px-3 py-2 font-semibold">{renderInlineMarkdown(header)}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/80">
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`} className="bg-white dark:bg-slate-800/40">
                  {headers.map((_, cellIndex) => <td key={`cell-${rowIndex}-${cellIndex}`} className="whitespace-nowrap px-3 py-2 align-top text-foreground">{renderInlineMarkdown(row[cellIndex] || '')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (bullet) {
      listItems.push(bullet[1]);
      continue;
    }
    flushList();
    if (line.trim()) {
      blocks.push(<p key={`paragraph-${index}`} className="mb-2.5 last:mb-0">{renderInlineMarkdown(line)}</p>);
    }
  }
  flushList();

  return <div className="text-sm leading-5 text-foreground">{blocks}</div>;
};

export const I4AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);
  const [panelSize, setPanelSize] = useState(null);
  const [draft, setDraft] = useState('');
  const [previewQuery, setPreviewQuery] = useState('');
  const [answer, setAnswer] = useState('');
  const [chart, setChart] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const panelRef = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const conversationIdRef = useRef(createConversationId());

  useEffect(() => {
    const stopInteraction = () => {
      dragState.current = null;
      resizeState.current = null;
      setIsDragging(false);
    };
    const resetPanelOnMobile = () => {
      if (window.innerWidth < 640) {
        setPanelPosition(null);
        setPanelSize(null);
      }
    };
    const moveWindow = (event) => {
      if (resizeState.current) {
        const { pointerX, pointerY, originWidth, originHeight, originLeft, originTop } = resizeState.current;
        const maxWidth = window.innerWidth - originLeft - 24;
        const maxHeight = window.innerHeight - originTop - 24;
        setPanelSize({
          width: Math.max(Math.min(360, maxWidth), Math.min(maxWidth, originWidth + event.clientX - pointerX)),
          height: Math.max(Math.min(480, maxHeight), Math.min(maxHeight, originHeight + event.clientY - pointerY)),
        });
        return;
      }
      if (!dragState.current) return;
      const { originX, originY, pointerX, pointerY, width, height } = dragState.current;
      setPanelPosition({
        x: Math.max(24, Math.min(window.innerWidth - width - 24, originX + event.clientX - pointerX)),
        y: Math.max(24, Math.min(window.innerHeight - height - 24, originY + event.clientY - pointerY)),
      });
    };
    window.addEventListener('pointermove', moveWindow);
    window.addEventListener('pointerup', stopInteraction);
    window.addEventListener('resize', resetPanelOnMobile);
    resetPanelOnMobile();
    return () => {
      window.removeEventListener('pointermove', moveWindow);
      window.removeEventListener('pointerup', stopInteraction);
      window.removeEventListener('resize', resetPanelOnMobile);
    };
  }, []);

  useEffect(() => {
    if (!open || window.innerWidth < 640) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPosition({
        x: Math.max(24, Math.min(rect.left, window.innerWidth - rect.width - 48)),
        y: Math.max(24, Math.min(rect.top, window.innerHeight - rect.height - 48)),
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const startDragging = (event) => {
    const target = event.target;
    if (
      window.innerWidth < 640
      || event.button !== 0
      || (target instanceof Element && target.closest('[data-ibi-window-control]'))
    ) return;
    event.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = {
      originX: rect.left,
      originY: rect.top,
      pointerX: event.clientX,
      pointerY: event.clientY,
      width: rect.width,
      height: rect.height,
    };
    setIsDragging(true);
  };

  const startResizing = (event) => {
    if (window.innerWidth < 640) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    resizeState.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      originWidth: rect.width,
      originHeight: rect.height,
      originLeft: rect.left,
      originTop: rect.top,
    };
    setPanelPosition({ x: rect.left, y: rect.top });
    setPanelSize({ width: rect.width, height: rect.height });
  };

  const submitQuestion = async (event) => {
    event.preventDefault();
    const query = draft.trim();
    if (!query || isSending) return;

    setPreviewQuery(query);
    setDraft('');
    setAnswer('');
    setChart(null);
    setSources([]);
    setError('');
    setIsSending(true);

    try {
      const response = await api.post('/assistant/query', {
        message: query,
        conversation_id: conversationIdRef.current,
      });
      setAnswer(response.data.answer || 'No pude generar una respuesta.');
      setChart(isInventoryChart(response.data.chart) ? response.data.chart : null);
      setSources(response.data.sources || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'No se pudo procesar la consulta. Intentá nuevamente.');
    } finally {
      setIsSending(false);
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submitQuestion(event);
  };

  const startNewConversation = async () => {
    if (isSending) return;
    const previousConversationId = conversationIdRef.current;
    conversationIdRef.current = createConversationId();
    setDraft('');
    setPreviewQuery('');
    setAnswer('');
    setChart(null);
    setSources([]);
    setError('');
    try {
      await api.delete('/assistant/conversation', { data: { conversation_id: previousConversationId } });
    } catch {
      // La nueva sesión ya está aislada aunque la limpieza remota no esté disponible.
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-2xl border border-primary/30 bg-slate-950/95 p-1.5 pr-3 shadow-[0_12px_32px_rgba(8,47,73,0.32)] backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-[0_16px_38px_rgba(8,145,178,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:ring-offset-slate-950 sm:bottom-6 sm:right-6"
          aria-label="Abrir Ibi"
          data-testid="i4-assistant-launcher"
        >
          <span className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-primary/20 bg-white shadow-inner">
            <span className="pointer-events-none absolute inset-1 z-10 rounded-lg border border-primary/15" />
            <img src={robotAssistant} alt="" className="h-full w-full scale-[1.05] object-contain transition-transform duration-300 group-hover:scale-[1.1]" />
          </span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm font-semibold leading-4 text-white">Ibi</span>
            <span className="mt-0.5 block text-[11px] leading-3 text-cyan-100/65">Consultá tu inventario</span>
          </span>
          <Sparkles className="hidden h-4 w-4 text-primary sm:block" strokeWidth={1.5} />
        </button>
      )}

      <DialogPrimitive.Root open={open} onOpenChange={setOpen} modal={false}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            ref={panelRef}
            aria-describedby={undefined}
            onPointerDownOutside={(event) => event.preventDefault()}
            style={{
              ...(panelPosition && { left: `${panelPosition.x}px`, top: `${panelPosition.y}px` }),
              ...(panelSize && { width: `${panelSize.width}px`, height: `${panelSize.height}px` }),
            }}
            className="fixed inset-0 z-50 flex h-dvh w-full flex-col overflow-hidden bg-slate-50 shadow-2xl outline-none sm:inset-auto sm:left-[max(1.5rem,calc(100vw-464px))] sm:top-[max(1.5rem,calc(100dvh-704px))] sm:h-[min(680px,calc(100dvh-48px))] sm:min-h-[480px] sm:w-[440px] sm:min-w-[360px] sm:max-h-[calc(100dvh-48px)] sm:max-w-[calc(100vw-48px)] sm:rounded-3xl sm:border sm:border-slate-200/80 sm:bg-slate-50/95 sm:shadow-[0_24px_70px_rgba(8,47,73,0.32)] sm:backdrop-blur dark:bg-slate-900 dark:sm:border-slate-700/80 dark:sm:bg-slate-900/95"
          >
            <header
              onPointerDown={startDragging}
              className={`relative shrink-0 select-none overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 text-left ${isDragging ? 'cursor-grabbing' : 'sm:cursor-grab'}`}
            >
              <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
              <div className="absolute -bottom-12 left-10 h-24 w-48 rounded-full bg-blue-500/10 blur-3xl" />
              <div className="relative flex items-center justify-between gap-2.5 px-4 py-1 sm:px-4">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-cyan-300/25 bg-white shadow-inner">
                    <img src={robotAssistant} alt="Robot de Ibi" className="h-full w-full scale-[1.05] object-contain" />
                  </div>
                  <div className="min-w-0">
                    <DialogPrimitive.Title className="text-base font-semibold text-white">Ibi</DialogPrimitive.Title>
                    <span className="mt-0.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-100/75">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
                      Solo lectura
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={startNewConversation}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-cyan-50/80 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Nueva conversación con Ibi"
                    title="Nueva conversación"
                    data-ibi-window-control
                    disabled={isSending}
                  >
                    <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-cyan-50/80 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
                    aria-label="Minimizar Ibi"
                    title="Minimizar"
                    data-ibi-window-control
                  >
                    <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-cyan-50/80 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
                      aria-label="Cerrar Ibi"
                      title="Cerrar"
                      data-ibi-window-control
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </DialogPrimitive.Close>
                </div>
              </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Hola, soy Ibi.</p>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">¿Qué querés revisar hoy del inventario?</p>
                </div>
              </div>
            </div>

            {previewQuery && (
              <div className="mt-6 space-y-3">
                <div className="ml-8 rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm leading-5 text-primary-foreground">
                  {previewQuery}
                </div>
                {isSending && (
                  <div className="mr-5 flex items-center gap-2 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-3 text-sm text-muted-foreground shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
                    <LoaderCircle className="h-4 w-4 animate-spin text-primary" strokeWidth={1.5} />
                    Voy a revisar las relaciones del inventario...
                  </div>
                )}
                {answer && (
                  <div className="mr-5 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-3 text-sm leading-5 text-foreground shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
                    <AssistantAnswer answer={answer} />
                    <InventoryChart chart={chart} />
                    {sources.length > 0 && (
                      <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-700">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Fuentes del inventario</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {sources.map((source) => (
                            <a
                              key={`${source.kind}-${source.id}`}
                              href={source.href}
                              className="rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition hover:bg-primary/20"
                            >
                              {source.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {error && (
                  <div className="mr-5 flex gap-2 rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-3.5 py-3 text-sm leading-5 text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>

            <form onSubmit={submitQuestion} className="border-t border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
            <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-1 shadow-sm transition focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15 dark:border-slate-700 dark:bg-slate-800">
              <Textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                placeholder="Consultá sobre activos, aplicaciones o servidores..."
                className="min-h-[44px] resize-none border-0 bg-transparent px-2 py-1.5 pr-10 text-sm shadow-none focus-visible:ring-0"
                aria-label="Consulta para Ibi"
              />
              <Button type="submit" size="icon" className="absolute bottom-2 right-2 h-7 w-7 rounded-lg" aria-label="Enviar consulta" disabled={isSending || !draft.trim()}>
                {isSending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Send className="h-3.5 w-3.5" strokeWidth={1.5} />}
              </Button>
            </div>
            </form>
            <div
              aria-hidden="true"
              onPointerDown={startResizing}
              className="absolute bottom-0 right-0 hidden h-6 w-6 cursor-se-resize sm:block"
            >
              <span className="absolute bottom-1.5 right-1.5 h-2.5 w-2.5 border-b border-r border-slate-400/80 dark:border-slate-500" />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
};
